import type { Point, SceneNode } from "../scene/model";
import type { AffineMatrix } from "../geometry/affine";
import { transformPoint } from "../geometry/affine";
import { polygonOutline, polygonParameters } from "./shapes";

export interface PolygonCorner {
  entry: Point;
  control: Point;
  exit: Point;
}
function vertices(node: SceneNode) {
  return polygonOutline(polygonParameters(node)).points.map((p) => ({
    x: p.x * node.geometry.width!,
    y: p.y * node.geometry.height!,
  }));
}
function edgeLimit(points: Point[], i: number) {
  const p = points[i]!,
    before = points[(i + points.length - 1) % points.length]!,
    after = points[(i + 1) % points.length]!;
  return (
    0.45 *
    Math.min(
      Math.hypot(before.x - p.x, before.y - p.y),
      Math.hypot(after.x - p.x, after.y - p.y),
    )
  );
}
export function polygonCornerLimit(node: SceneNode) {
  const points = vertices(node);
  return Math.max(...points.map((_, i) => edgeLimit(points, i)));
}
/** Original polygon semantics: edge setbacks and quadratic corners, including concave vertices. */
export function polygonCorners(node: SceneNode): PolygonCorner[] {
  const points = vertices(node),
    radius = Number(node.geometry.cornerRadius ?? 0);
  return points.map((control, i) => {
    const r = Math.min(radius, edgeLimit(points, i));
    const toward = (p: Point) => {
      const length = Math.hypot(p.x - control.x, p.y - control.y);
      return {
        x: control.x + ((p.x - control.x) * r) / length,
        y: control.y + ((p.y - control.y) * r) / length,
      };
    };
    return {
      control,
      entry: toward(points[(i + points.length - 1) % points.length]!),
      exit: toward(points[(i + 1) % points.length]!),
    };
  });
}
export function quadraticPoint(c: PolygonCorner, t: number): Point {
  const u = 1 - t;
  return {
    x: u * u * c.entry.x + 2 * u * t * c.control.x + t * t * c.exit.x,
    y: u * u * c.entry.y + 2 * u * t * c.control.y + t * t * c.exit.y,
  };
}
/** Chord error <= tolerance until the bounded 8192-segment budget is reached. */
export function roundedPolygonOutline(
  node: SceneNode,
  tolerance = 0.01,
): Point[] {
  const curves = polygonCorners(node),
    maximum = Math.floor(8192 / curves.length);
  return curves.flatMap((c) => {
    const bend = Math.hypot(
      c.entry.x - 2 * c.control.x + c.exit.x,
      c.entry.y - 2 * c.control.y + c.exit.y,
    );
    const steps = Math.min(
      maximum,
      Math.max(1, Math.ceil(Math.sqrt(bend / (4 * Math.max(1e-8, tolerance))))),
    );
    return Array.from({ length: steps + 1 }, (_, i) =>
      quadraticPoint(c, i / steps),
    );
  });
}
/** Affine-transformed quadratic extrema give exact bounds without a renderer or sampled mesh. */
export function polygonCornerExtrema(
  node: SceneNode,
  matrix: AffineMatrix,
): Point[] {
  const world = (p: Point) =>
    transformPoint(
      { x: p.x / node.geometry.width!, y: p.y / node.geometry.height! },
      matrix,
    );
  return polygonCorners(node).flatMap((c) => {
    const curve = {
      entry: world(c.entry),
      control: world(c.control),
      exit: world(c.exit),
    };
    const result = [curve.entry, curve.exit];
    for (const axis of ["x", "y"] as const) {
      const denominator =
        curve.entry[axis] - 2 * curve.control[axis] + curve.exit[axis];
      if (Math.abs(denominator) < 1e-12) continue;
      const t = (curve.entry[axis] - curve.control[axis]) / denominator;
      if (t > 0 && t < 1) result.push(quadraticPoint(curve, t));
    }
    return result;
  });
}
/** Cutting convex tips and filling concave valleys preserves the radial polygon's kernel. */
export function roundedPolygonTriangles(node: SceneNode, tolerance: number) {
  const points = roundedPolygonOutline(node, tolerance),
    { center } = polygonOutline(polygonParameters(node));
  return new Float32Array(
    points.flatMap((p, i) => {
      const next = points[(i + 1) % points.length]!;
      return [
        center.x + 0.5,
        center.y + 0.5,
        p.x / node.geometry.width! + 0.5,
        p.y / node.geometry.height! + 0.5,
        next.x / node.geometry.width! + 0.5,
        next.y / node.geometry.height! + 0.5,
      ];
    }),
  );
}
